import _ from 'lodash';
import { useCallback } from 'react';
import { KEY_RECOIL, AUTO_EVENTS, DEVICE_TAB_TYPES } from '@/shared/config';
import {
  selectActiveTab,
  selectChangeStripData,
  selectDeviceEventCount,
  selectEmptySelectedKey,
  selectEventStripsData,
  useReportStore,
} from '@/entities/report';
import { MAP_AUTO_EVENT_TYPE } from '../constants';

/**
 * Backs the left-hand event-type column of the Strip Management tab with its active tab, per-tab counts and unsaved-change markers. Counting reads either the device event count map or the filtered strip data, and switching tabs clears both the strip and device selections, page indexes and totals before storing the new tab.
 *
 * @returns An object carrying the active tab, empty selection key and the tab click, count and unsaved-change helpers.
 */
const useStripLeftColumn = () => {
  const keyRecoil = KEY_RECOIL.STRIP_MANAGEMENT;
  const keyDeviceRecoil = KEY_RECOIL.DEVICE_MANAGEMENT;

  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const setActiveButtonAction = useReportStore((s) => s.actions.setActiveTab);
  /**
   * Writes the active event-type tab of the strip tab into the report store. Forwards the value to the store action together with the TAB_3 key.
   *
   * @param v - The new active tab, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setActiveButton = (v: Parameters<typeof setActiveButtonAction>[1]) =>
    setActiveButtonAction(keyRecoil, v);
  const eventStripsData = useReportStore(selectEventStripsData(keyRecoil));
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Writes the strip tab page index into the report store. Forwards the value to the store action together with the TAB_3 key.
   *
   * @param v - The new page index, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Writes the selected strip of the strip tab into the report store. Forwards the value to the store action together with the TAB_3 key.
   *
   * @param v - The new selected strip, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const setTotalStrip = useReportStore((s) => s.actions.setStripTotalStrip);
  const setDeviceTotalStrip = useReportStore((s) => s.actions.setTotalStripDevice);
  const emptySelectedKey = useReportStore(selectEmptySelectedKey(keyRecoil));
  const changeStripData = useReportStore(selectChangeStripData(keyRecoil));
  /**
   * Writes the selected strip of the device tab into the report store. Forwards the value to the selected-strip action together with the TAB_4 device key.
   *
   * @param v - The new selected strip, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setDeviceSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyDeviceRecoil, v);
  /**
   * Writes the device tab page index into the report store. Forwards the value to the page-index action together with the TAB_4 device key.
   *
   * @param v - The new page index, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setDevicePageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyDeviceRecoil, v);
  const deviceChangeData = useReportStore(selectChangeStripData(keyDeviceRecoil));
  const deviceEventCount = useReportStore(selectDeviceEventCount(keyDeviceRecoil));

  /**
   * Switches the left column to another event-type tab. Does nothing when the tab is already active, otherwise clears the strip and device selections, resets both page indexes to zero, zeroes both strip totals and stores the new tab.
   *
   * @param tab - The left-column tab key that was clicked.
   * @returns Nothing.
   */
  const onClickTab = (tab: any) => {
    if (activeButton === tab) {
      return;
    }

    setSelectedStrip(null);
    setDeviceSelectedStrip(null);
    setPageIndex((prev) => (prev.index === 0 ? prev : { index: 0 }));
    setDevicePageIndex((prev) => (prev.index === 0 ? prev : { index: 0 }));
    setTotalStrip(0);
    setDeviceTotalStrip(0);
    setActiveButton(tab);
  };

  /**
   * Counts the strips shown under one left-column event tab. Reads device tabs from the device event count map, yields null while no strips are loaded, and otherwise counts the strips whose type belongs to that tab, or all of them for the combined auto-events tab.
   *
   * @param tab - The left-column tab key being counted.
   * @returns The strip count for that tab, or null while no strips are loaded.
   */
  const getCount = (tab: any) => {
    if (DEVICE_TAB_TYPES.includes(tab)) {
      return (deviceEventCount as any)?.[tab] || 0;
    }
    if (_.isEmpty(eventStripsData)) return null;
    const filterType = MAP_AUTO_EVENT_TYPE[tab];
    if (!filterType || tab === AUTO_EVENTS) return eventStripsData.length;
    const filteredData = _.filter(eventStripsData, (event) => filterType.includes(event.type));
    return filteredData.length;
  };

  /**
   * Reports whether a left-column tab holds unsaved strip edits. Checks the device change list by filter type for device tabs, all non-device changes for the combined auto-events tab, and otherwise the changes whose type belongs to that tab.
   *
   * @param tab - The left-column tab key being checked.
   * @returns True when that tab has pending strip changes.
   */
  const isUnsavedChange = useCallback(
    (tab: any) => {
      const isDeviceEvent = DEVICE_TAB_TYPES.includes(tab);
      if (isDeviceEvent) {
        const filterEvents = _.filter(deviceChangeData, (event) => event.filterType === tab);
        return !_.isEmpty(filterEvents);
      }
      if (tab === AUTO_EVENTS) {
        const filterEvents = _.filter(changeStripData, (event) => !event.isDeviceEvent);
        return !_.isEmpty(filterEvents);
      }
      const filterType = MAP_AUTO_EVENT_TYPE[tab];
      const filteredData = _.filter(changeStripData, (event) => filterType.includes(event.type));
      return !_.isEmpty(filteredData);
    },
    [changeStripData, deviceChangeData],
  );

  return {
    activeButton,
    emptySelectedKey,
    onClickTab,
    getCount,
    isUnsavedChange,
  };
};

export default useStripLeftColumn;
